import {
  UI_DIVIDER_HEIGHT_PX,
  UI_FONT_BODY_TREATISE,
  UI_FONT_DISPLAY,
  UI_FONT_NUMERIC,
  UI_INK,
  UI_IVORY,
  UI_PAGE_CONTENT_PAD_PX,
  UI_PAGE_EDGE_PX,
  UI_PAGE_FILIGRANA_ALPHA,
  UI_PAGE_HEDERA_PX,
  UI_PAGE_MARGIN_PX,
  UI_PAGE_RULE_GAP_PX,
  UI_PAPER_DARK,
  UI_PAPER_LIGHT,
  UI_SEAL_RED,
  UI_TEXTURE_HEDERA_URL,
  UI_TEXTURE_PARCHMENT_URL,
  UI_TEXTURE_SEGNO_URL,
} from '../config/constants';
import type { ButtonVariant, PanelButton } from './button';

// ─────────────────────────────────────────────────────────────
// Texture cache + font readiness
// ─────────────────────────────────────────────────────────────

let parchmentImg: HTMLImageElement | null = null;
let segnoImg: HTMLImageElement | null = null;
let hederaImg: HTMLImageElement | null = null;
const repaintListeners = new Set<() => void>();

/**
 * Subscribe to texture/font-ready events. Panels register so they can repaint
 * once async resources land; otherwise the first draw shows the procedural
 * fallback only.
 */
export function onAssetsReady(fn: () => void): () => void {
  repaintListeners.add(fn);
  return () => repaintListeners.delete(fn);
}

function notifyReady(): void {
  for (const fn of repaintListeners) fn();
}

/** Kick off async loading of the page textures and required fonts. */
export function preloadUiAssets(): void {
  loadImage(UI_TEXTURE_PARCHMENT_URL)
    .then((img) => {
      parchmentImg = img;
      notifyReady();
    })
    .catch(() => undefined);
  loadImage(UI_TEXTURE_SEGNO_URL)
    .then((img) => {
      segnoImg = img;
      notifyReady();
    })
    .catch(() => undefined);
  loadImage(UI_TEXTURE_HEDERA_URL)
    .then((img) => {
      hederaImg = img;
      notifyReady();
    })
    .catch(() => undefined);
  if (typeof document !== 'undefined' && document.fonts) {
    Promise.all([
      document.fonts.load('700 64px Cinzel'),
      document.fonts.load('600 32px "JetBrains Mono"'),
      document.fonts.load('400 32px "EB Garamond"'),
      document.fonts.load('600 32px "EB Garamond"'),
    ])
      .then(notifyReady)
      .catch(() => undefined);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

// ─────────────────────────────────────────────────────────────
// Font helpers
// ─────────────────────────────────────────────────────────────

export function displayFont(px: number, weight: 500 | 700 = 700): string {
  return `${weight} ${Math.round(px)}px ${UI_FONT_DISPLAY}`;
}
export function numericFont(px: number, weight: 500 | 700 = 700): string {
  return `${weight} ${Math.round(px)}px ${UI_FONT_NUMERIC}`;
}
export function treatiseBodyFont(px: number, weight: 400 | 600 = 400): string {
  return `${weight} ${Math.round(px)}px ${UI_FONT_BODY_TREATISE}`;
}

// ─────────────────────────────────────────────────────────────
// Treatise page — printed Opera Nova page: paper edge-to-edge,
// double ink rule frame, hedera fleurons, segno dei tagli filigrana.
// ─────────────────────────────────────────────────────────────

export type PageFrameOptions = {
  /** Segno watermark opacity override (e.g. stronger on the colpo-picker step). */
  filigranaAlpha?: number;
};

/**
 * Paint the full treatise-page background: paper, deckled edge, segno
 * filigrana, double ink rule frame and corner hederas. Caller draws
 * title/content/buttons on top.
 */
export function paintPagePanelFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  opts?: PageFrameOptions,
): void {
  paintPaper(ctx, w, h);
  paintPaperEdge(ctx, w, h);
  paintSegnoFiligrana(ctx, w, h, opts?.filigranaAlpha ?? UI_PAGE_FILIGRANA_ALPHA);
  paintRuleFrame(ctx, w, h);
}

function paintPaper(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  if (parchmentImg) {
    const pattern = ctx.createPattern(parchmentImg, 'repeat');
    if (pattern) {
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, w, h);
      // Brighten towards printed-page paper (the raw texture reads too dark).
      ctx.fillStyle = 'rgba(255,244,210,0.38)';
      ctx.fillRect(0, 0, w, h);
      return;
    }
  }
  const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.7);
  grad.addColorStop(0, UI_PAPER_LIGHT);
  grad.addColorStop(1, UI_PAPER_DARK);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

/** Darkened deckle band at the paper edge — separates the page from the room. */
function paintPaperEdge(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const e = UI_PAGE_EDGE_PX;
  for (const [gy0, gy1, fy] of [
    [0, e, 0],
    [h, h - e, h - e],
  ] as const) {
    const grad = ctx.createLinearGradient(0, gy0, 0, gy1);
    grad.addColorStop(0, 'rgba(86,58,24,0.45)');
    grad.addColorStop(1, 'rgba(86,58,24,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, fy, w, e);
  }
  for (const [gx0, gx1, fx] of [
    [0, e, 0],
    [w, w - e, w - e],
  ] as const) {
    const grad = ctx.createLinearGradient(gx0, 0, gx1, 0);
    grad.addColorStop(0, 'rgba(86,58,24,0.45)');
    grad.addColorStop(1, 'rgba(86,58,24,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(fx, 0, e, h);
  }
  // Cut edge of the sheet
  ctx.strokeStyle = 'rgba(60,38,14,0.55)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, w - 2, h - 2);
}

/** Marozzo's segno dei tagli, multiply-blended into the paper at the centre. */
function paintSegnoFiligrana(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  alpha: number,
): void {
  if (!segnoImg) return;
  const maxH = Math.min(w, h) * 0.62;
  const scale = Math.min(maxH / segnoImg.height, (w * 0.7) / segnoImg.width);
  const dw = segnoImg.width * scale;
  const dh = segnoImg.height * scale;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(segnoImg, (w - dw) / 2, (h - dh) / 2 + h * 0.04, dw, dh);
  ctx.restore();
}

/** Double ink rule frame with hedera fleurons over the corners. */
function paintRuleFrame(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const m = UI_PAGE_MARGIN_PX;
  const g = UI_PAGE_RULE_GAP_PX;
  ctx.save();
  ctx.strokeStyle = 'rgba(36,20,8,0.85)'; // UI_INK, printed-ink coverage
  ctx.lineWidth = 3;
  ctx.strokeRect(m, m, w - m * 2, h - m * 2);
  ctx.strokeStyle = 'rgba(36,20,8,0.65)';
  ctx.lineWidth = 1;
  ctx.strokeRect(m + g, m + g, w - (m + g) * 2, h - (m + g) * 2);
  ctx.restore();

  const s = UI_PAGE_HEDERA_PX;
  const corners: Array<[number, number, number]> = [
    [m, m, -Math.PI / 4],
    [w - m, m, Math.PI / 4],
    [w - m, h - m, (Math.PI * 3) / 4],
    [m, h - m, (-Math.PI * 3) / 4],
  ];
  for (const [cx, cy, rot] of corners) drawHedera(ctx, cx, cy, s, rot, 0.9);
}

/** Draw the Aldine-leaf fleuron tinted to ink, centred at (cx,cy). */
function drawHedera(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  sizePx: number,
  rotationRad: number,
  alpha: number,
): void {
  if (!hederaImg) return;
  const off = document.createElement('canvas');
  off.width = sizePx;
  off.height = sizePx;
  const octx = off.getContext('2d');
  if (!octx) return;
  const scale = Math.min(sizePx / hederaImg.width, sizePx / hederaImg.height);
  const dw = hederaImg.width * scale;
  const dh = hederaImg.height * scale;
  octx.drawImage(hederaImg, (sizePx - dw) / 2, (sizePx - dh) / 2, dw, dh);
  octx.globalCompositeOperation = 'source-in';
  octx.fillStyle = UI_INK;
  octx.fillRect(0, 0, sizePx, sizePx);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy);
  ctx.rotate(rotationRad);
  ctx.drawImage(off, -sizePx / 2, -sizePx / 2);
  ctx.restore();
}

/**
 * Cinzel title centred near the top of the page, over an ink double rule
 * divider with a hedera at the centre.
 */
export function paintPageTitleRow(
  ctx: CanvasRenderingContext2D,
  title: string,
  w: number,
  h: number,
): void {
  const inset = UI_PAGE_MARGIN_PX + UI_PAGE_RULE_GAP_PX;
  const titleY = inset + h * 0.055;
  const sizePx = Math.round(h * 0.085);
  ctx.save();
  ctx.font = displayFont(sizePx, 700);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = UI_INK;
  ctx.fillText(title.toUpperCase(), w / 2, titleY, w - inset * 2 - 40);

  const dividerY = titleY + sizePx + 16;
  paintPageDivider(ctx, w / 2, dividerY, Math.min(w * 0.5, 400));
  ctx.restore();
}

function paintPageDivider(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  fullW: number,
): void {
  const halfW = fullW / 2;
  const gap = UI_DIVIDER_HEIGHT_PX * 1.2;
  ctx.save();
  ctx.strokeStyle = 'rgba(36,20,8,0.8)';
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - halfW, cy);
  ctx.lineTo(cx - gap, cy);
  ctx.moveTo(cx + gap, cy);
  ctx.lineTo(cx + halfW, cy);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(36,20,8,0.4)';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(cx - halfW + 8, cy + 4);
  ctx.lineTo(cx - gap, cy + 4);
  ctx.moveTo(cx + gap, cy + 4);
  ctx.lineTo(cx + halfW - 8, cy + 4);
  ctx.stroke();
  ctx.restore();
  drawHedera(ctx, cx, cy, UI_DIVIDER_HEIGHT_PX * 1.5, 0, 0.85);
}

/** Engraved cartouche buttons for the treatise page. */
export function paintPageButton(
  ctx: CanvasRenderingContext2D,
  btn: PanelButton,
  variant: ButtonVariant = 'primary',
): void {
  const { x, y, w, h } = btn.rect;
  const r = 5;
  ctx.save();

  if (variant === 'primary') {
    // Seal-red cartouche: ink rule outside, ivory hairline inside the wax.
    roundRectPath(ctx, x, y, w, h, r);
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, '#c65f3e');
    grad.addColorStop(1, UI_SEAL_RED);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(36,20,8,0.85)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    roundRectPath(ctx, x + 4, y + 4, w - 8, h - 8, Math.max(2, r - 2));
    ctx.strokeStyle = 'rgba(240,230,210,0.45)';
    ctx.lineWidth = 1;
    ctx.stroke();
    paintPageButtonLabel(ctx, btn, UI_IVORY, h * 0.42, true);
  } else if (variant === 'active') {
    // Inverted letterpress: solid ink block, paper-coloured text.
    roundRectPath(ctx, x, y, w, h, r);
    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, '#3c2812');
    grad.addColorStop(1, UI_INK);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(36,20,8,0.9)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    paintPageButtonLabel(ctx, btn, UI_PAPER_LIGHT, h * 0.42, true);
  } else {
    // Secondary: open cartouche, double ink rule on paper.
    roundRectPath(ctx, x, y, w, h, r);
    ctx.fillStyle = 'rgba(36,20,8,0.05)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(36,20,8,0.8)';
    ctx.lineWidth = 1.8;
    ctx.stroke();
    roundRectPath(ctx, x + 4, y + 4, w - 8, h - 8, Math.max(2, r - 2));
    ctx.strokeStyle = 'rgba(36,20,8,0.35)';
    ctx.lineWidth = 0.8;
    ctx.stroke();
    paintPageButtonLabel(ctx, btn, UI_INK, h * 0.42, false);
  }

  ctx.restore();
}

function paintPageButtonLabel(
  ctx: CanvasRenderingContext2D,
  btn: PanelButton,
  color: string,
  sizePx: number,
  display: boolean,
): void {
  ctx.fillStyle = color;
  ctx.font = display ? displayFont(sizePx, 700) : treatiseBodyFont(sizePx, 600);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Clamp to the button width so long labels condense instead of overflowing the rect.
  const maxWidth = btn.rect.w - sizePx * 0.6;
  ctx.fillText(btn.label, btn.rect.x + btn.rect.w / 2, btn.rect.y + btn.rect.h / 2, maxWidth);
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─────────────────────────────────────────────────────────────
// Bounds of the content area (inside the rule frame + padding)
// ─────────────────────────────────────────────────────────────

export type ContentBounds = { x: number; y: number; w: number; h: number };

export function contentBounds(canvasW: number, canvasH: number): ContentBounds {
  const inset = UI_PAGE_MARGIN_PX + UI_PAGE_RULE_GAP_PX + UI_PAGE_CONTENT_PAD_PX;
  return {
    x: inset,
    y: inset,
    w: canvasW - inset * 2,
    h: canvasH - inset * 2,
  };
}
